import type { I18nService } from '@core/i18n-legacy';
import {
  Blueprint,
  DatasetBase,
  HeaderRow,
  MAX_SECOND_DIMENSION_LENGTH,
  PIVOT_TABLE_COUNT_ID,
  PivotTableData,
  ReportDashboardComponentKind,
  ReportDashboardComponentViewMode,
  SummaryData,
  TableData,
  ValueRow,
  VisualizationType,
  ReportsTranslations,
} from '@core/reports-types';
import { getComponentVisualizationType } from '@core/reports-util';
import { SupportedNumberFormat, valueFormatter } from '@core/utils-legacy';

export const pivotTable = (
  translations: ReportsTranslations,
  kind: ReportDashboardComponentKind,
): Blueprint<PivotTableData> => ({
  match: (component, meta) =>
    component.kind === kind &&
    getComponentVisualizationType(component) === VisualizationType.PivotTable,

  factory: ({ component, dataset: originalDataset, meta }) => {
    let dataset = originalDataset;
    if (dataset.dimensions.length > 3) {
      dataset = dataset.truncateDataset(
        'second',
        MAX_SECOND_DIMENSION_LENGTH,
        true,
        translations.i18n.translate('tlr.group_name.other'),
      );
    }
    if (meta.truncateDataset) {
      const secondaryEntries = dataset.datasetSize.cols - 1;
      const truncateLength =
        typeof meta.truncateDataset === 'number'
          ? meta.truncateDataset
          : meta.truncateDataset(component, dataset);
      const firstDimensionLimit = Math.ceil(truncateLength / secondaryEntries);
      dataset = dataset.truncateDataset('first', firstDimensionLimit);
    }
    const raw = dataset.getRawData();
    const headerRow = <HeaderRow>raw.slice(0, 1).pop();
    const valueRows = <Array<ValueRow<number>>>raw.slice(1);

    const group = component.secondaryGroup ?? component.primaryGroup!;

    return {
      vis: {
        dimensions: <const>[
          headerRow[0],
          {
            id: group,
            name: translations.translateGroup(group),
          },
          {
            id: PIVOT_TABLE_COUNT_ID,
            name: dataset.metric.resource,
          },
        ],
        values: valueRows.map(
          ([dimension, ...values]) =>
            <const>[
              dimension,
              values.map((x, index) => ({ ...headerRow[index + 1] })),
              values.map(value => ({
                formatted: valueFormatter(
                  value,
                  {
                    unitType: dataset.metric.unitType,
                    unitPrecision: dataset?.meta?.unitPrecision ?? dataset.metric.unitPrecision,
                  },
                  translations.i18n,
                  {
                    number: { format: SupportedNumberFormat.IntlStandard },
                  },
                ),
                value,
              })),
            ],
        ),
      },
      dataset,
    };
  },
});

export const table = (
  i18n: I18nService,
  kind: ReportDashboardComponentKind,
): Blueprint<TableData> => ({
  match: component =>
    component.kind === kind && getComponentVisualizationType(component) === VisualizationType.Table,
  factory: ({ component, dataset: originalDataset, metric, meta }) => {
    let dataset = originalDataset;
    if (meta.truncateDataset) {
      const truncateLength =
        typeof meta.truncateDataset === 'number'
          ? meta.truncateDataset
          : meta.truncateDataset(component, dataset);
      dataset = dataset.truncateDataset('first', truncateLength);
    }
    const raw = dataset.getRawData();
    const headerRow = <HeaderRow>raw.slice(0, 1).pop();
    const valueRows = <Array<ValueRow<number>>>raw.slice(1);

    return {
      vis: <TableData>(<unknown>{
        dimensions: headerRow,
        values: valueRows.map(([dimension, ...values]) => [
          dimension,
          ...values.map(value => ({
            formatted: valueFormatter(
              value,
              {
                unitType: dataset.metric.unitType,
                unitPrecision: dataset?.meta?.unitPrecision ?? dataset.metric.unitPrecision,
              },
              i18n,
              {
                number: { format: SupportedNumberFormat.IntlStandard },
              },
            ),
            value,
          })),
        ]),
      }),
      dataset,
    };
  },
});

export const summaryMapper = (dataset: DatasetBase, i18n: I18nService) => {
  const raw = dataset.getRawData();
  const rawValues = raw.slice(1);

  // Ratios are already reduced to a single value by the dataset and can be null
  // (denominator of 0), which summing would silently turn into 0.
  const isRatio = dataset.metric.unitType === 'ratio';

  const items: SummaryData['items'] = rawValues.map((row, rowIndex) => {
    const values = <number[]>row.slice(1);
    const value = isRatio ? values[0] : values.reduce((acc: number, v: number) => acc + v, 0);
    return {
      clickable: value > 0,
      dimensions: {
        primary: row[0],
      },
      header: {
        primary: row[0]?.name,
      },
      breadcrumbs: [],
      value: valueFormatter(
        value,
        {
          unitType: dataset.metric.unitType,
          unitPrecision: dataset?.meta?.unitPrecision ?? dataset.metric.unitPrecision,
        },
        i18n,
        {
          number: { format: SupportedNumberFormat.IntlStandard },
        },
      ),
      rawValue: value,
    };
  });

  return <SummaryData>{
    dimensions: raw[0],
    values: raw.slice(1),
    items,
  };
};

export const summary = (
  i18n: I18nService,
  kind: ReportDashboardComponentKind,
): Blueprint<SummaryData> => ({
  match: {
    kind,
    viewMode: ReportDashboardComponentViewMode.Summary,
  },

  factory: ({ dataset: ogDataset, meta, component }) => {
    let dataset = ogDataset;
    if (meta.truncateDataset) {
      const truncateLength =
        typeof meta.truncateDataset === 'number'
          ? meta.truncateDataset
          : meta.truncateDataset(component, dataset);
      dataset = dataset.truncateDataset('first', truncateLength);
    }
    return {
      vis: summaryMapper(dataset, i18n),
      dataset,
    };
  },
});
